import React, { useEffect, useMemo, useRef, useState } from 'react';
import TextEditor from 'shared-components/components/TextEditor/TextEditor';

import {
  GET_EDITABLE_DOCUMENT,
  UPDATE_EDITABLE_DOCUMENT_LAST_VIEWED_AT,
} from 'shared-components/components/TextEditor/TextEditorQueries';
import { DocumentType } from '../../Interface';
import { Box, Button, Card, CardContent, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ArrowForward, ChevronLeft, ChevronRight, Edit } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@apollo/client';
import DocumentDisplayPanel from '../DocumentDisplayPanel';
import { ToolbarGroup, ToolbarSeparator } from 'shared-components/components/TipTap/tiptap-ui-primitive/toolbar';
import { MarkButton } from 'shared-components/components/TipTap/tiptap-ui/mark-button';
import { TextAlignButton } from 'shared-components/components/TipTap/tiptap-ui/text-align-button';
import { Spacer } from 'shared-components/components/TipTap/tiptap-ui-primitive/spacer';
import { ListDropdownMenu } from 'shared-components/components/TipTap/tiptap-ui/list-dropdown-menu';
import { BlockquoteButton } from 'shared-components/components/TipTap/tiptap-ui/blockquote-button';
import { HeadingDropdownMenu } from 'shared-components/components/TipTap/tiptap-ui/heading-dropdown-menu';
import { UndoRedoButton } from 'shared-components/components/TipTap/tiptap-ui/undo-redo-button';
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar';
import StandardDialog from 'shared-components/components/StandardDialog/StandardDialog';

export interface AutoDocEditorDocument {
  editableDocumentId: string;
  name: string;
  previewUrl: string;
  viewed: boolean;
}

interface AutoDocEditorProps {
  document: AutoDocEditorDocument;
  updateDocumentStatus: (editableDocumentId: string, hasUncommittedChanges: boolean, viewed: boolean) => void;
  initialEditMode?: boolean;
  onApprove?: () => Promise<void> | void;
  onApproveClick?: () => void;
  onClose?: () => void;
  showVersionNavigator?: boolean;
  approveText?: string;
  editingChipText?: string;
  showApproveIndicator?: boolean;
}

const formatTimestamp = (timestamp: string | null | undefined): string | null => {
  if (!timestamp) return null;
  try {
    return dayjs(timestamp).format('MMM D, YYYY [at] h:mm A');
  } catch {
    return null;
  }
};

const getDocumentStatus = (editableDocument: any): 'approved' | 'new_version' => {
  if (editableDocument && !editableDocument.isAutoGenerated && editableDocument.confirmedAt !== null) {
    return 'approved';
  }
  return 'new_version';
};

const VersionNavigator = ({
  version,
  disabled = false,
  prevDisabled,
  nextDisabled,
  onPreviousVersion,
  onNextVersion,
  isDraft = false,
}: {
  version: number | null;
  disabled?: boolean;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  onPreviousVersion?: () => void;
  onNextVersion?: () => void;
  isDraft?: boolean;
}): JSX.Element | null => {
  if (version === null) return null;
  const prevDisabledResolved = disabled || prevDisabled === true;
  const nextDisabledResolved = disabled || nextDisabled === true;

  const versionText = (
    <Typography
      variant="body2"
      color={disabled ? 'text.secondary' : undefined}
      component="span"
      sx={{ textTransform: 'uppercase' }}>
      Version {version + 1}
      {isDraft && ' (draft)'}
    </Typography>
  );

  if (disabled) {
    return <span>{versionText}</span>;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ color: prevDisabledResolved && nextDisabledResolved ? 'text.secondary' : 'text.primary' }}>
      <Tooltip title="View previous version" placement="top">
        <span>
          <IconButton
            size="small"
            color="inherit"
            disabled={prevDisabledResolved}
            onClick={onPreviousVersion}
            aria-label="View previous version"
            data-testid="autodoc-version-prev">
            <ChevronLeft />
          </IconButton>
        </span>
      </Tooltip>
      {versionText}
      <Tooltip title="View next version" placement="top">
        <span>
          <IconButton
            size="small"
            color="inherit"
            disabled={nextDisabledResolved}
            onClick={onNextVersion}
            aria-label="View next version"
            data-testid="autodoc-version-next">
            <ChevronRight />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

const ViewModeToolbar = ({
  title,
  lastSaved,
  onEditDocument,
  version,
  status,
  documentRegenerated = true,
  editDisabled = false,
  showVersionNavigator = true,
}: {
  title: string;
  lastSaved: string | null;
  onEditDocument: () => void;
  version: number | null;
  status: 'approved' | 'new_version';
  documentRegenerated: boolean;
  editDisabled?: boolean;
  showVersionNavigator?: boolean;
}): JSX.Element => {
  const isApproved = status === 'approved';
  const showNewVersionBanner = status === 'new_version' && version !== 0 && version !== null;

  return (
    <Stack
      direction="row"
      spacing={2}
      height="2.875rem"
      alignItems="center"
      justifyContent="space-between"
      sx={{ padding: '0.5rem 1rem' }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="subtitle1">{title}</Typography>

        {isApproved && <Chip label="Approved" color="success" size="small" variant="outlined" />}
        {showNewVersionBanner && (
          <Tooltip
            title="New version generated from Careplan updates"
            placement="top"
            arrow
            componentsProps={{ tooltip: { sx: { maxWidth: 'none' } } }}>
            <Chip label="New version" size="small" variant="outlined" color="info" />
          </Tooltip>
        )}
        {showVersionNavigator && <VersionNavigator version={version} disabled />}
        {isApproved && (
          <Typography variant="body2" color="text.secondary">
            Last approved at {lastSaved}
          </Typography>
        )}
      </Stack>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<Edit />}
        onClick={onEditDocument}
        disabled={editDisabled}
        sx={{ textTransform: 'uppercase' }}
        data-testid="autodoc-edit-button">
        Edit
      </Button>
    </Stack>
  );
};

const EditModeToolbar = ({
  title,
  data,
  mutations,
  isEditorReady,
  approveDisabled = false,
  onDiscardRequested,
  onExitToPdfView,
  currentVersion,
  hasPreviousVersion,
  hasNextVersion,
  onRequestVersionChange,
  onApprove,
  onApproveClick,
  showVersionNavigator = true,
  approveText = 'Approve',
  editingChipText = 'Editing',
  showApproveIndicator = false,
}: {
  title: string;
  data:
    | {
        editableDocument?: {
          lastEditedAt?: string | null;
          confirmedAt?: string | null;
          version?: number | null;
          activeVersion?: boolean | null;
        };
      }
    | null
    | undefined;
  mutations: {
    discard: { handle: () => Promise<unknown> | void; loading: boolean };
    confirm: { handle: () => void; loading: boolean };
  };
  isEditorReady: boolean;
  approveDisabled?: boolean;
  onDiscardRequested: () => void;
  onExitToPdfView: () => void;
  currentVersion: number | null;
  hasPreviousVersion: boolean;
  hasNextVersion: boolean;
  onRequestVersionChange: (version: number) => void;
  onApprove?: () => Promise<void> | void;
  onApproveClick?: () => void;
  showVersionNavigator?: boolean;
  approveText?: string;
  editingChipText?: string;
  showApproveIndicator?: boolean;
}): JSX.Element => {
  const hasUnapprovedChanges = Boolean(data?.editableDocument?.lastEditedAt && !data?.editableDocument?.confirmedAt);
  const isViewingNonActiveVersion = data?.editableDocument?.activeVersion === false;
  const discardShowsRevertToast =
    !isViewingNonActiveVersion && data?.editableDocument?.version !== 0 && data?.editableDocument?.confirmedAt === null;

  const confirmDisabled = onApprove
    ? !isEditorReady || mutations.confirm.loading || mutations.discard.loading || isViewingNonActiveVersion
    : !isEditorReady ||
      mutations.confirm.loading ||
      mutations.discard.loading ||
      isViewingNonActiveVersion ||
      !hasUnapprovedChanges;

  const handleDiscardClick = async () => {
    if (isViewingNonActiveVersion) {
      onExitToPdfView();
      return;
    }
    onDiscardRequested();
    await mutations.discard.handle();
    onExitToPdfView();
  };

  const discardDisabled =
    !isEditorReady ||
    mutations.discard.loading ||
    mutations.confirm.loading ||
    (!isViewingNonActiveVersion && !hasUnapprovedChanges);

  return (
    <Stack
      direction="row"
      height="2.875rem"
      alignItems="center"
      sx={{ padding: '0.5rem 1rem', justifyContent: 'space-between', borderBottom: '0.0625rem solid #E0E0E0' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexGrow: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle1" component="div">
            {title}
          </Typography>
          <Chip label={editingChipText} size="small" variant="outlined" color="warning" />
          {showVersionNavigator && (
            <VersionNavigator
              version={currentVersion}
              prevDisabled={!hasPreviousVersion || mutations.discard.loading}
              nextDisabled={!hasNextVersion || mutations.discard.loading}
              onPreviousVersion={() => onRequestVersionChange(currentVersion! - 1)}
              onNextVersion={() => onRequestVersionChange(currentVersion! + 1)}
              isDraft={hasUnapprovedChanges}
            />
          )}
        </Stack>
      </Box>
      <Stack direction="row" spacing={2} sx={{ marginLeft: 'auto' }}>
        <Tooltip
          title={discardShowsRevertToast ? 'This will revert back to the last version' : ''}
          placement="top"
          arrow>
          <span>
            <Button
              size="small"
              variant="text"
              color="primary"
              disabled={discardDisabled}
              onClick={handleDiscardClick}
              sx={{ textTransform: 'uppercase', fontSize: '13px' }}
              data-testid="autodoc-discard-button">
              Discard
            </Button>
          </span>
        </Tooltip>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={confirmDisabled}
          startIcon={
            showApproveIndicator ? <ArrowForward fontSize="small" data-testid="autodoc-approve-arrow" /> : undefined
          }
          onClick={() => {
            onApproveClick?.();
            mutations.confirm.handle();
          }}
          sx={{ textTransform: 'uppercase', fontSize: '13px' }}
          data-testid="autodoc-approve-button">
          {approveText}
        </Button>
      </Stack>
    </Stack>
  );
};

const CustomEditorToolbar = (): JSX.Element => {
  return (
    <>
      <Spacer />

      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2]} showNormalText />
        <ListDropdownMenu types={['bulletList', 'orderedList', 'taskList']} />
        <BlockquoteButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="underline" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <Spacer />
    </>
  );
};

const AutoDocEditor = ({
  document,
  updateDocumentStatus,
  initialEditMode = false,
  onApprove,
  onApproveClick,
  onClose,
  showVersionNavigator = true,
  approveText = 'Approve',
  editingChipText = 'Editing',
  showApproveIndicator = false,
}: AutoDocEditorProps): JSX.Element => {
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [showDiscardToast, setShowDiscardToast] = useState(false);
  const [confirmVersionSwitchTarget, setConfirmVersionSwitchTarget] = useState<number | null>(null);
  /** When non-null, we are viewing this specific version; null means show active version. */
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  /** True once DocumentDisplayPanel has finished loading the document (iframe onLoad). */
  const [documentPanelLoaded, setDocumentPanelLoaded] = useState(false);

  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const discardMutationRef = useRef<(() => void) | null>(null);
  const saveMutationRef = useRef<(() => void) | null>(null);
  /** Used when user confirms "discard and go to version X" so that version becomes active and edits/saves work. */
  const discardThenRevertRef = useRef<((version: number) => void) | null>(null);

  const documentQuery = useQuery(GET_EDITABLE_DOCUMENT, {
    variables: {
      editableDocumentVersionGroupId: document.editableDocumentId,
      ...(viewingVersion != null && { version: viewingVersion }),
    },
    skip: !document.editableDocumentId,
    fetchPolicy: 'cache-and-network',
  });

  const { data, loading: isDocumentLoading } = documentQuery;
  const currentVersion = data?.editableDocument?.version ?? null;
  const hasUncommittedChanges = useMemo(
    () => Boolean(data?.editableDocument?.lastEditedAt && !data?.editableDocument?.confirmedAt),
    [data?.editableDocument?.lastEditedAt, data?.editableDocument?.confirmedAt],
  );
  const isEditView = isEditing || hasUncommittedChanges;

  const nextVersionQuery = useQuery(GET_EDITABLE_DOCUMENT, {
    variables: {
      editableDocumentVersionGroupId: document.editableDocumentId,
      version: (currentVersion ?? 0) + 1,
    },
    skip: !document.editableDocumentId || currentVersion == null || !isEditView,
    fetchPolicy: 'cache-and-network',
  });
  const hasNextVersion = Boolean(nextVersionQuery.data?.editableDocument);
  const hasPreviousVersion = (currentVersion ?? 0) > 0;

  const [updateEditableDocumentLastViewedAt] = useMutation(UPDATE_EDITABLE_DOCUMENT_LAST_VIEWED_AT, {
    refetchQueries: [GET_EDITABLE_DOCUMENT],
    awaitRefetchQueries: true,
  });
  const lastSaved = data?.editableDocument?.confirmedAt
    ? dayjs(data.editableDocument.confirmedAt).format('MMM D, YYYY [at] h:mm A')
    : null;

  const handleRequestVersionChange = (targetVersion: number) => {
    if (hasUncommittedChanges) {
      setConfirmVersionSwitchTarget(targetVersion);
      return;
    }
    setViewingVersion(targetVersion);
  };

  const handleConfirmVersionSwitchModal = () => {
    if (confirmVersionSwitchTarget === null) return;
    setConfirmVersionSwitchTarget(null);
    // Discard then revert so the target version becomes active on the backend; then edits/saves work.
    discardMutationRef.current?.();
  };

  const handleDiscard = () => {
    setIsEditing(false);
    setViewingVersion(null);
    updateDocumentStatus(document.editableDocumentId, false, true);
    if (data?.editableDocument?.version !== 0 && data?.editableDocument?.confirmedAt === null) {
      setShowDiscardToast(true);
    }
  };

  const handleExitToPdfView = () => {
    setViewingVersion(null);
    setIsEditing(false);
    updateDocumentStatus(document.editableDocumentId, false, true);
    onClose?.();
  };

  useEffect(() => {
    if (!isEditView) {
      discardMutationRef.current = null;
      discardThenRevertRef.current = null;
    }
    setHasLocalEdits(false);
  }, [isEditView]);

  // If the document has not been viewed, update the document status to show the new document.
  useEffect(() => {
    if (!data) return;
    const firstTimeViewing = !data?.editableDocument?.lastViewedAt;
    if (firstTimeViewing) {
      updateEditableDocumentLastViewedAt({
        variables: {
          editableDocumentVersionGroupId: document.editableDocumentId,
        },
      });
      updateDocumentStatus(document.editableDocumentId, hasUncommittedChanges, false);
    } else {
      updateDocumentStatus(document.editableDocumentId, hasUncommittedChanges, true);
    }
  }, [data]);

  // First time editing
  useEffect(() => {
    const handleFirstTimeEditing = async () => {
      if (hasLocalEdits) {
        const version = (await saveMutationRef.current?.()) ?? null;
        if (version !== null) {
          setViewingVersion(version);
        }
      }
    };
    handleFirstTimeEditing();
  }, [hasLocalEdits]);

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        padding: 0,
        borderRadius: '0.25rem',
        borderWidth: '0.0625rem',
      }}
      elevation={0}>
      <CardContent
        sx={{
          padding: 0,
          paddingBottom: '0 !important',
          height: '100%',
        }}>
        {' '}
        {isEditView ? (
          <Box
            sx={{
              height: '100%',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
              '& .simple-editor-content': {
                width: '100%',
                maxWidth: '71em',
                marginLeft: 'auto',
                marginRight: 'auto',
              },
              '& .simple-editor': {
                fontSize: '95%',
                color: '#000000 !important',
                '& h2': {
                  color: '#000000 !important',
                },
              },
            }}>
            <TextEditor
              editableDocumentId={document.editableDocumentId}
              documentQuery={documentQuery}
              renderActionsBar={({
                data: editorData,
                mutations,
                isEditorReady,
                onDiscardRequested,
                approveDisabled,
              }) => {
                discardMutationRef.current = mutations.discard.handle;
                discardThenRevertRef.current = mutations.discardThenRevertToVersion?.handle ?? null;
                saveMutationRef.current = mutations.save.handle;
                return (
                  <EditModeToolbar
                    title={document.name}
                    data={editorData}
                    mutations={{
                      discard: mutations.discard,
                      confirm: mutations.confirm,
                    }}
                    isEditorReady={isEditorReady}
                    approveDisabled={approveDisabled}
                    onDiscardRequested={onDiscardRequested}
                    onExitToPdfView={handleExitToPdfView}
                    currentVersion={editorData?.editableDocument?.version ?? null}
                    hasPreviousVersion={hasPreviousVersion}
                    hasNextVersion={hasNextVersion}
                    onRequestVersionChange={handleRequestVersionChange}
                    onApprove={onApprove}
                    onApproveClick={onApproveClick}
                    showVersionNavigator={showVersionNavigator}
                    approveText={approveText}
                    editingChipText={editingChipText}
                    showApproveIndicator={showApproveIndicator}
                  />
                );
              }}
              onConfirm={async () => {
                setHasLocalEdits(false);
                updateDocumentStatus(document.editableDocumentId, false, true);
                await Promise.resolve(onApprove?.()).finally(() => setIsEditing(false));
              }}
              onDiscard={handleDiscard}
              autosaveEnabled={true}
              autosaveInterval={3000}
              onSave={(version) => {
                if (version !== null) {
                  setViewingVersion(version);
                }
              }}
              onContentChange={() => {
                setHasLocalEdits(true);
                updateDocumentStatus(document.editableDocumentId, true, true);
              }}
            />
          </Box>
        ) : (
          <Stack sx={{ height: '100%' }}>
            <ViewModeToolbar
              title={document.name}
              lastSaved={lastSaved}
              status={getDocumentStatus(data?.editableDocument)}
              onEditDocument={() => {
                if (isDocumentLoading || !documentPanelLoaded) return;
                setIsEditing(true);
                setViewingVersion(data?.editableDocument?.groupActiveVersionNumber ?? 0);
              }}
              version={data?.editableDocument?.version}
              documentRegenerated={document.viewed}
              editDisabled={isDocumentLoading || !documentPanelLoaded}
              showVersionNavigator={showVersionNavigator}
            />
            <Stack sx={{ padding: 0, minWidth: '49.625rem', flex: '1' }}>
              <DocumentDisplayPanel
                url={document.previewUrl}
                embedded={true}
                onLoadStateChange={setDocumentPanelLoaded}
              />
            </Stack>
          </Stack>
        )}
        <StandardDialog
          title="Discard current changes?"
          open={confirmVersionSwitchTarget !== null}
          onClose={() => setConfirmVersionSwitchTarget(null)}
          onSubmit={handleConfirmVersionSwitchModal}
          closeText="Cancel"
          submitText="Continue">
          <Typography>
            You’re about to load a previously approved version of this document. Any unapproved changes in the current
            version will be discarded.
          </Typography>
        </StandardDialog>
        <NotificationSnackbar
          open={showDiscardToast}
          onClose={() => setShowDiscardToast(false)}
          message="Changes discarded - reverted to last approved version."
          marginBottom="5.6875rem"
          type="notification"
        />
      </CardContent>
    </Card>
  );
};

export default AutoDocEditor;
